import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil, switchMap, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ColDef } from 'ag-grid-community';
import { TableConfig } from '../../../shared/components/table/table.interfaces';
import { ActionConfig } from '../../../shared/components/table/table-action-configs';
import { SearchGroupItem } from '../../../shared/interfaces/common.model';
import * as TableConfigs from '../asset-management-table-configuration';
import { FRAMEWORK_COMPONENTS } from '../asset-management-table-configuration';
import { AssetManagementService, CardType } from '../asset-management-service';
import { MyRequestTableService } from './my-request-table.service';
import { OnboardingRequestsTableService } from './onboarding-requests-table.service';
import { OffboardingRequestsTableService } from './offboarding-requests-table.service';
import { AssetAllocatedTableService } from './asset-allocated-table.service';
import type { DomLayoutType } from 'ag-grid-community';
import { InventoryTableService } from './inventory-table.service';
import { VendorStatsTableService } from './vendor-stats-table.service';
import { DamageAnalyticsTableService } from './damage-analytics-table.service';
import { ExportService } from '@app/shared/services/export.service';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ResourceProfilePageComponent } from '../cwr-offboardingCard/Offboarding-DetailsScreen/resource-profile-page/resource-profile-page.component';
import { AddCommentsComponent } from '../cwr-offboardingCard/Offboarding-DetailsScreen/add-comments/add-comments.component';
import { LOCATION_OPTIONS } from '../my-request/my-request-form/my-request-form.constants';
import { ToasterService } from '@app/shared/services/toaster.service';
import { AlertService } from '@services/alert.service';

interface TableServiceContract {
  data$: Observable<any[]>;
  handleAction?(action: string, data: any): void;
  refreshData?(): void;
}

@Component({
  selector: 'app-asset-management-table',
  templateUrl: './asset-management-table.component.html',
  styleUrl: './asset-management-table.component.scss',
})
export class AssetManagementTableComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  // Table state
  columns: ColDef[] = [];
  tableConfig: TableConfig = TableConfigs.MY_REQUEST_TABLE_CONFIG;
  actions: ActionConfig[] = [];
  buttonTitle = 'Create New Request';
  rowData: any[] = [];
  originalRowData: any[] = [];
  selectedCard: CardType | null = null;
  selectedRows: any[] = [];
  pageTitle = 'Asset Management';
  maxTableHeight: string = '500px';
  isLoading = false; // Card-specific loading state
  isCardLoading = false; // Simple card loading state

  // Search field properties
  searchValues: SearchGroupItem = {
    label: 'Search',
    placeholder: 'Search records...',
    cssClass: 'search-field',
  };
  searchTerm: string = '';
  dateLabel = 'Select Date';
  startDatePlaceholder = 'Start Date';
  endDatePlaceholder = 'End Date';
  locationOptions = LOCATION_OPTIONS;
  selectedLocation: string = 'All';
  selectedEmployerType: string = 'CWR';
  employerTypeOptions = [
    { label: 'CWR', value: 'CWR' },
    { label: 'Employee', value: 'Employee' }
  ];

  private BREADCRUMB_FLOW_MAP: Record<string, string> = {
    inventory: 'inventoryDetails',
    'inventory-order': 'inventoryOrderDetails',
    'damage-analytics': 'damageAnalyticsDetails',
    onboarding: 'onboardingDetails',
    offboarding: 'offboardingDetails',
    'vendor-stats': 'vendorStatsDetails',
    'asset-allocated': 'assetAllocatedDetails',
    'My-Request': 'myRequestDetails',
  };

  get currentFlowKey(): string | undefined {
    return this.selectedCard
      ? this.BREADCRUMB_FLOW_MAP[this.selectedCard]
      : undefined;
  }

  gridOptions: any;
  frameworkComponents = FRAMEWORK_COMPONENTS;

  private serviceRegistry!: Record<CardType, TableServiceContract>;

  constructor(
    private readonly myRequestService: MyRequestTableService,
    private readonly onboardingService: OnboardingRequestsTableService,
    private readonly offboardingService: OffboardingRequestsTableService,
    private readonly assetAllocatedService: AssetAllocatedTableService,
    private readonly inventoryService: InventoryTableService,
    private readonly vendorStatsService: VendorStatsTableService,
    private readonly damageAnalyticsService: DamageAnalyticsTableService,
    private readonly exportService: ExportService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly assetManagementService: AssetManagementService,
    private readonly toaster: ToasterService,
    private alertService: AlertService,
  ) {}

  private dataSubscription: any;

  ngOnInit(): void {

    this.gridOptions = {
      context: {
        componentParent: this,
        onResourceClick: this.myRequestService.onResourceClick.bind(
          this.myRequestService,
        ),
      },
      domLayout: 'autoHeight' as DomLayoutType,
      getRowClass: (params: any) => {
        if (this.selectedCard === 'damage-analytics') {
          const status = params.data?.damageCostStatus || params.data?.status;
          if (status && (status === 'cancelled' || status === 'Cancelled')) {
            return 'cancelled-row';
          }
        }
        return '';
      },
    };
    this.serviceRegistry = {
      'My-Request': this.myRequestService,
      onboarding: this.onboardingService,
      offboarding: this.offboardingService,
      'asset-allocated': this.assetAllocatedService,
      inventory: this.inventoryService,
      'inventory-order': this.inventoryService, // same service, different data stream
      'vendor-stats': this.vendorStatsService,
      'damage-analytics': this.damageAnalyticsService,
      header: this.myRequestService,
    } as Record<CardType, TableServiceContract>;

    // Get initial card selection - check current route first, then localStorage
    const currentUrl = this.router.url;
    const urlSegments = currentUrl.split('/');
    const currentRoutePath = urlSegments[urlSegments.length - 1];

    let initialCard: CardType = 'My-Request';

    // Map current route to card type
    switch (currentRoutePath) {
      case 'asset-allocated':
        initialCard = 'asset-allocated';
        break;
      case 'upcoming-onboarding':
        initialCard = 'onboarding';
        break;
      case 'upcoming-offboarding':
        initialCard = 'offboarding';
        break;
      case 'my-request':
        initialCard = 'My-Request';
        break;
      case 'damage-analytics':
        initialCard = 'damage-analytics';
        break;
      case 'vendor-stats':
        initialCard = 'vendor-stats';
        break;
      case 'accessories-management':
        initialCard = 'inventory';
        break;
      case 'inventory-order':
        initialCard = 'inventory-order';
        break;
      default:
        // Fall back to localStorage if route doesn't match
        const cardFromStorage = localStorage.getItem('selectedCard') as CardType;
        initialCard = cardFromStorage || 'My-Request';
        break;
    }


    // Set default if nothing is stored and no route match
    if (!localStorage.getItem('selectedCard')) {
      localStorage.setItem('selectedCard', initialCard);
    }

    // Initialize with the selected card (only one call)
    this.selectedCard = initialCard;
    if (this.selectedCard === 'inventory-order') {
      this.selectedLocation = this.inventoryService.getCurrentProcuredLocation() || 'All';
    } else if (this.selectedCard === 'damage-analytics' || this.selectedCard === 'inventory') {
      this.selectedLocation = this.damageAnalyticsService.getCurrentLocation() || 'All';
    }
    this.configureTableForCard(this.selectedCard);
    this.setButtonTitle();
    this.loadInitialDataForCard(this.selectedCard);

    // Subscribe to loading states from all services for their respective cards
    this.assetAllocatedService.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe(loading => {
        if (this.selectedCard === 'asset-allocated') {
          this.isCardLoading = loading;
        }
      });

    // Add loading subscriptions for other services if they have loading states
    if (this.vendorStatsService.loading$) {
      this.vendorStatsService.loading$
        .pipe(takeUntil(this.destroy$))
        .subscribe(loading => {
          if (this.selectedCard === 'vendor-stats') {
            this.isCardLoading = loading;
          }
        });
    }

    if (this.damageAnalyticsService.loading$) {
      this.damageAnalyticsService.loading$
        .pipe(takeUntil(this.destroy$))
        .subscribe(loading => {
          if (this.selectedCard === 'damage-analytics') {
            this.isCardLoading = loading;
          }
        });
    }

    // Subscribe to router navigation events for dynamic card switching
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        // Extract the last segment from the URL
        const urlSegments = event.url.split('/');
        const routePath = urlSegments[urlSegments.length - 1];
        let targetCard: CardType = this.selectedCard || 'My-Request';

        switch (routePath) {
          case 'asset-allocated':
            targetCard = 'asset-allocated';
            break;
          case 'upcoming-onboarding':
            targetCard = 'onboarding';
            break;
          case 'upcoming-offboarding':
            targetCard = 'offboarding';
            break;
          case 'my-request':
            targetCard = 'My-Request';
            break;
          case 'damage-analytics':
            targetCard = 'damage-analytics';
            break;
          case 'vendor-stats':
            targetCard = 'vendor-stats';
            break;
      case 'accessories-management':
            targetCard = 'inventory';
            break;
          case 'inventory-order':
            targetCard = 'inventory-order';
            break;
          default:
            return; // Don't switch if we don't recognize the route
        }

        // Only switch if different from current and if we have a valid target
        if (this.selectedCard !== targetCard && targetCard) {
          this.switchToCard(targetCard);
        }
      });

    this.subscribeToCurrentCardData();
  }

  private subscribeToCurrentCardData(): void {
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }

    const dataObservable = this.getDataObservableForCard(this.selectedCard);
    if (!dataObservable) {
      return;
    }

    this.dataSubscription = dataObservable
      .subscribe({
        next: (data: any[]) => {
          this.originalRowData = [...data];
          this.rowData = [...data];
          if (this.searchTerm) {
            this.onSearch(this.searchTerm);
          } else {
            this.applyEmployerFilter();
          }
        },
        error: (error) => {
        }
      });

    // Global handler for resource profile popup (for asset-allocated and others)
    (window as any).openResourceProfile = (employeeId: string) => {
      this.openResourceProfileDialog(employeeId);
    };

    // Global handler for asset resource profile popup (specifically for asset-allocated)
    (window as any).openAssetResourceProfile = (employeeId: string) => {
      this.openResourceProfileDialog(employeeId);
    };

    // Global handler for Date of Return field change
    (window as any).onDateOfReturnChange = (recordId: string, employeeId: string, dateValue: string) => {
      this.onDateOfReturnChange(recordId, employeeId, dateValue);
    };
  }

  private openResourceProfileDialog(employeeId: string): void {
    // Find the row data by employeeId to get the full record
    const row = this.rowData.find((r: any) => r.employeeId === employeeId);
    if (!row) {
      return;
    }

    // Fetch additional data if needed via API
    this.assetManagementService.getOffboardingGetData(employeeId).subscribe({
      next: (response) => {
        const record = response.data?.[0] || row; // Use API data if available, fallback to row data

        const dialogConfig: MatDialogConfig = {
          width: '1400px',
          height: '900px',
          panelClass: ['resource-profile-dialog-panel', 'center-dialog'],
          autoFocus: false,
          disableClose: false,
          data: {
            record,
            offboardingData: response.data, // Pass the full API response to avoid duplicate call
            employeeId
          },
        };

        this.dialog.open(ResourceProfilePageComponent, dialogConfig);
      },
      error: (error) => {
        // Fallback: open dialog with just the row data
        const dialogConfig: MatDialogConfig = {
          width: '1400px',
          height: '900px',
          panelClass: ['resource-profile-dialog-panel', 'center-dialog'],
          autoFocus: false,
          disableClose: false,
          data: { record: row },
        };

        this.dialog.open(ResourceProfilePageComponent, dialogConfig);
      }
    });
  }

  onSearch(value: string): void {
    this.searchTerm = value?.toLowerCase().trim();
    const visibleFields = this.columns
      .map(col => col.field)
      .filter((field): field is string => typeof field === 'string' && !!field);

    const additionalFields = ['employeeId', 'jobTitle'];
    const allSearchableFields = [...new Set([...visibleFields, ...additionalFields])];

    this.rowData = this.searchTerm
      ? this.originalRowData.filter((row) =>
          allSearchableFields.some((field) =>
            String(row[field] ?? '')
              .toLowerCase()
              .includes(this.searchTerm)
          )
        )
      : [...this.originalRowData];
 
    this.applyEmployerFilter();
  }

  onEmployerTypeChange(event: any): void {
    this.selectedEmployerType = event?.value || event;
    // Reset to original data first
    this.rowData = [...this.originalRowData];
    // Apply search filter if exists
    if (this.searchTerm) {
      this.onSearch(this.searchTerm);
    } else {
      this.applyEmployerFilter();
    }
  }

  private applyEmployerFilter(): void {
    if (
      this.selectedCard !== 'onboarding' &&
      this.selectedCard !== 'offboarding' &&
      this.selectedCard !== 'My-Request'
    ) {
      return;
    }

    if (this.selectedEmployerType === 'CWR') {
      this.rowData = this.rowData.filter((row) => row.poiType === 'CWR');
    } else if (this.selectedEmployerType === 'Employee') {
      this.rowData = this.rowData.filter((row) => row.poiType !== 'CWR');
    }
  }

  handleLocationChange(event: any): void {


    const location = event?.value || event;
    this.selectedLocation = location;

    if (this.selectedCard === 'inventory') {
      this.inventoryService.getInventorySummaryData(
        this.inventoryService.currentStartDate,
        this.inventoryService.currentEndDate,
        this.selectedLocation,
      );
    } else
    if (this.selectedCard === 'inventory-order') {
      this.inventoryService.getProcuredSummaryData(
        this.inventoryService.currentOrderStartDate,
        this.inventoryService.currentOrderEndDate,
        this.selectedLocation,
      );
    } else
    if (this.selectedCard === 'damage-analytics') {
      this.damageAnalyticsService.updateLocationAndRefresh(location);
    }
  }

  onClear(): void {
    this.rowData = [...this.originalRowData];
    this.applyEmployerFilter();
  }

  handleDateRangeChange(filteredData: any): void {
    switch (this.selectedCard) {
      case 'My-Request':
        this.myRequestService.updateDateRange(filteredData.startDate, filteredData.endDate);
        this.myRequestService.loadAssetRequests(
          filteredData.startDate,
          filteredData.endDate,
        );
        break;
      case 'vendor-stats':
        this.vendorStatsService.getVendorSummaryData(
          filteredData.startDate,
          filteredData.endDate,
        );
        break;
      case 'damage-analytics':
        this.damageAnalyticsService.updateDateRange(filteredData.startDate, filteredData.endDate);
        this.damageAnalyticsService.getDamageAnalyticsData(
          filteredData.startDate,
          filteredData.endDate,

        );
        break;
      case 'offboarding':
        this.offboardingService.loadOffboardingData(
          filteredData.startDate,
          filteredData.endDate,
        );
        break;
      case 'onboarding':
        this.onboardingService.updateDateRange(filteredData.startDate, filteredData.endDate);
        this.onboardingService.loadOnboardingData(
          filteredData.startDate,
          filteredData.endDate,
        );
        break;
      case 'inventory':
        this.inventoryService.getInventorySummaryData(
          filteredData.startDate,
          filteredData.endDate,
          this.selectedLocation,
        );
        break;
      case 'inventory-order':
        this.inventoryService.getProcuredSummaryData(
          filteredData.startDate,
          filteredData.endDate,
          this.selectedLocation,
        );
        break;
      case 'asset-allocated':
        this.assetAllocatedService.getAssetAllocatedData(
          filteredData.startDate,
          filteredData.endDate,
        );
        break;
        this.assetAllocatedService.getAssetAllocatedData(
          filteredData.startDate,
          filteredData.endDate,
        );
        break;
      default:
        break;
    }
  }

  private configureTableForCard(cardType: CardType | null): void {
    const config = this.getCardTypeConfig(cardType);

    this.columns = config.columns;
    this.tableConfig = config.tableConfig;
    this.actions = config.actions;
    this.dateLabel = config.dateLabel || this.dateLabel;
    this.pageTitle = config.pageTitle || this.pageTitle;
  }

  private getDataObservableForCard(
    cardType: CardType | null,
  ): Observable<any[]> {
    const service = this.serviceRegistry[cardType || 'My-Request'];

    return cardType === 'inventory-order'
      ? this.inventoryService.dataProcured$ || this.inventoryService.data$
      : service?.data$ || this.myRequestService.data$;
  }

  handleTableAction(event: any): void {
    const actionData = event.rowData || event.data;
    const cardType = this.selectedCard || 'My-Request';
    const service = this.serviceRegistry[cardType];
    service?.handleAction
      ? service.handleAction(event.action, actionData)
      : null;
  }
  handleRowSelection(event: any): void {
    this.selectedRows = event;
  }

  handleResourceClick(event: any): void {}

  onActionClick(action: string, rowData: any): void {
    this.handleTableAction({ action, data: rowData });
  }

  // Button handlers
  handleAddNew(): void {
    const config = this.getCardTypeConfig(this.selectedCard);
    config.buttonAction ? config.buttonAction() : null;
  }

  // Conditional button visibility
  showCreateRequestButton(buttonUseCase: any): void {
    switch (this.selectedCard) {
      case 'My-Request':
        // this.myRequestService.openCreateRequestModal();
        this.buttonTitle = 'Create New Request';
        return;
      case 'offboarding':
        this.buttonTitle = 'Create Offboarding Request';
        // this.offboardingService.openOffboardingFormDialog();
        return;
      case 'damage-analytics':
        this.buttonTitle = 'Add New';
        // this.offboardingService.openOffboardingFormDialog();
        return;
      case 'inventory-order':
        this.buttonTitle = 'Add New Order Item';
        // this.inventoryService.openAddNewFormDialog();
        return;
    }
    // return this.selectedCard === 'My-Request';
  }

  // Set button title based on selected card
  private setButtonTitle(): void {
    const config = this.getCardTypeConfig(this.selectedCard);
    this.buttonTitle = config.buttonTitle || 'Create New Request';
  }

  navigateToDetailView(): void {
    this.handleAddNew(); // Reuse the same logic
  }

  onProcureItems(): void {
    this.inventoryService.openProcureItemDialog();
  }

  onBulkIssueUpload(): void {
    if (this.selectedCard === 'inventory-order') {
      this.inventoryService.openBulkProcuredUploadDialog();
    } else {
      this.inventoryService.openBulkIssueUploadDialog();
    }
  }


  private createAssetAllocatedCustomColumns(): any[] {
    const customColumns = TableConfigs.ASSET_ALLOCATED_TABLE_CONFIG.columns.map(col => {
      // Format Laptop S/N and Charger S/N in uppercase
      if (col.field === 'laptopSerialNumber' || col.field === 'chargerSerialNumber') {
        return {
          ...col,
          cellRenderer: 'CopyCellRenderer',
          valueGetter: (params: any) => {
            const value = params.data[col.field!];
            return value ? String(value).toUpperCase() : '';
          }
        };
      }



      // Format email column with ellipses and tooltip
      if (col.field === 'email') {
        return {
          ...col,
          flex: 0, // Remove flex to allow fixed width
          width: 250,
          maxWidth: 300,
          minWidth: 200,
          resizable: true,
          suppressSizeToFit: true,
          tooltipField: 'email', // Use AG Grid's built-in tooltip
          cellStyle: () => ({
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            padding: '8px 12px',
            display: 'block'
          })
        };
      }

      return col;
    });

    return customColumns;
  }

  download(): void {
   this.exportService.handleExport(this.selectedRows, this.columns,this.selectedCard || 'My-Request', );

  }

  handleRefresh(): void {
    const cardType = this.selectedCard || 'My-Request';
    const service = this.serviceRegistry[cardType];

    service?.refreshData ? service.refreshData() : null;
  }

  // Centralized card type -> config mapping
  private getCardTypeConfig(cardType: CardType | null): {
    columns: ColDef[];
    actions: ActionConfig[];
    tableConfig: TableConfig;
    buttonTitle?: string;
    dateLabel?: string;
    pageTitle?: string;
    buttonAction?: () => void;
  } {
    switch (cardType) {
      case 'onboarding':
        return {
          columns: TableConfigs.ONBOARDING_TABLE_CONFIG.columns,
          actions: TableConfigs.ONBOARDING_TABLE_CONFIG.actions,
          tableConfig: TableConfigs.ONBOARDING_TABLE_CONFIG,
          buttonTitle: 'Create New Request',
          pageTitle: 'Upcoming Onboarding',
          buttonAction: () => {
            this.onboardingService?.openCreateRequestModal();
          },
        } as any;
      case 'offboarding':
        return {
          columns: TableConfigs.OFFBOARDING_TABLE_CONFIG.columns,
          actions: TableConfigs.OFFBOARDING_TABLE_CONFIG.actions,
          tableConfig: TableConfigs.OFFBOARDING_TABLE_CONFIG,
          buttonTitle: 'Create Off-boarding Request',
          pageTitle: 'Upcoming Off-boarding',
          buttonAction: () => {
            this.offboardingService?.openOffboardingFormDialog();
          },
        } as any;
      case 'asset-allocated':
        // Create custom columns for asset-allocated table
        const assetAllocatedCustomColumns = this.createAssetAllocatedCustomColumns();

        // Create a custom action with increased column width for asset-allocated table
        const assetAllocatedActions = TableConfigs.ASSET_ALLOCATED_TABLE_CONFIG.actions.map(action => ({
          ...action,
          columnWidth: 335 // Set custom width for the action column
        }));

        return {
          columns: assetAllocatedCustomColumns,
          actions: assetAllocatedActions,
          tableConfig: TableConfigs.ASSET_ALLOCATED_TABLE_CONFIG,
          buttonTitle: 'Add or Replace Asset/Accessories',
          pageTitle: 'Assets Allocated',
          buttonAction: () => {
            this.assetAllocatedService.openAddNewAssetFormDialog();
          },
        } as any;
      case 'inventory':
        return {
          columns: TableConfigs.INVENTORY_TABLE_CONFIG.columns,
          actions: TableConfigs.INVENTORY_TABLE_CONFIG.actions,
          tableConfig: TableConfigs.INVENTORY_TABLE_CONFIG,
          buttonTitle: 'Issue New Item',
          pageTitle: 'Accessories Management',
          buttonAction: () => {
            this.inventoryService.openIssueNewItemFormDialog?.();
          },
        } as any;
      case 'inventory-order':
        return {
          columns: TableConfigs.INVENTORY_ORDER_TABLE_CONFIG.columns,
          actions: [],
          tableConfig: TableConfigs.INVENTORY_ORDER_TABLE_CONFIG,
          buttonTitle: 'Add New Order Item',
          pageTitle: 'Inventory',
          buttonAction: () => {
            this.inventoryService.openAddNewFormDialog?.();
          },
        } as any;
      case 'vendor-stats':
        return {
          columns: TableConfigs.VENDOR_STATS_TABLE_CONFIG.columns,
          actions: TableConfigs.VENDOR_STATS_TABLE_CONFIG.actions,
          tableConfig: TableConfigs.VENDOR_STATS_TABLE_CONFIG,
          pageTitle: 'Vendor Stats',
        } as any;
      case 'damage-analytics':
        return {
          columns: TableConfigs.DAMAGE_ANALYTICS_TABLE_CONFIG.columns,
          actions: TableConfigs.DAMAGE_ANALYTICS_TABLE_CONFIG.actions,
          tableConfig: TableConfigs.DAMAGE_ANALYTICS_TABLE_CONFIG,
          buttonTitle: 'Add Damage Record',
          pageTitle: 'Damage Analytics',
          buttonAction: () => {
            this.damageAnalyticsService.openFormDialog?.();
          },
        } as any;
      case 'header':
        // Fallback to My Request
        return {
          columns: TableConfigs.MY_REQUEST_TABLE_CONFIG.columns,
          actions: TableConfigs.MY_REQUEST_TABLE_CONFIG.actions,
          tableConfig: TableConfigs.MY_REQUEST_TABLE_CONFIG,
          buttonTitle: 'Create New Request',
          pageTitle: 'My Requests',
        } as any;
      case 'My-Request':
      default:
        return {
          columns: TableConfigs.MY_REQUEST_TABLE_CONFIG.columns,
          actions: TableConfigs.MY_REQUEST_TABLE_CONFIG.actions,
          tableConfig: TableConfigs.MY_REQUEST_TABLE_CONFIG,
          buttonTitle: 'Create New Request',
          pageTitle: 'My Requests',
          buttonAction: () => {
            this.myRequestService?.openCreateRequestModal();
          },
        } as any;
    }
  }

  ngOnDestroy(): void {
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Specialized add methods
  onAddNewDamageAnalytics(): void {
    this.damageAnalyticsService.openFormDialog?.();
  }

  onAddNewOrderInventory(): void {
    this.inventoryService.openAddNewFormDialog?.();
  }

  inventoryTabType(type: CardType) {
    this.configureTableForCard(type);
    localStorage.setItem('selectedCard', type);
    const routePath = type === 'inventory' ? 'accessories-management' : type;
    this.router.navigate(['asset-management/' + routePath]);
  }
  /**
   * Optimized card switching with selective data loading
   */
  switchToCard(card: CardType): void {
    if (this.selectedCard === card) return; // Already on this card

    // Reset loading states when switching cards
    this.isLoading = false;
    this.isCardLoading = false;

    this.selectedCard = card;
    localStorage.setItem('selectedCard', card);

    // Reset employer filter to default when switching cards
    this.selectedEmployerType = 'CWR';

    // Sync location filter for the tab being switched to
    if (card === 'inventory-order') {
      this.selectedLocation = this.inventoryService.getCurrentProcuredLocation() || 'All';
    } else if (card === 'inventory') {
      this.selectedLocation = this.inventoryService.getCurrentLocation() || 'All';
    }

    // Update UI configuration
    this.configureTableForCard(card);
    this.setButtonTitle();

    // Load data for the new card
    this.loadDataForCard(card);
  }  /**
   * Load data for a specific card with preload detection
   */
  private loadDataForCard(card: CardType): void {
    // Clear previous subscription
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }

    // Check if data is already preloaded (cached)
    const service = this.serviceRegistry[card];
    const isPreloaded = this.isDataPreloaded(card);

    if (isPreloaded) {
      // Data is cached, load instantly
      this.dataSubscription = this.getDataObservableForCard(card)
        .subscribe((data: any[]) => {
          this.originalRowData = [...data];
          this.rowData = [...data];
          this.applyEmployerFilter();
        });
    } else {
      // Set loading state for asset-allocated card specifically
      if (card === 'asset-allocated') {
        this.isLoading = true;
      }

      // Load data fresh
      this.loadInitialDataForCard(card);
      this.dataSubscription = this.getDataObservableForCard(card)
        .subscribe((data: any[]) => {
          this.originalRowData = [...data];
          this.rowData = [...data];
          this.applyEmployerFilter();
        });
    }
  }  /**
   * Check if data is already cached/preloaded for a card
   */
  private isDataPreloaded(card: CardType): boolean {
    const service = this.serviceRegistry[card];
    return false;
  }

  /**
   * Refresh data for the current card only
   */
  refreshCurrentCard(): void {
    if (!this.selectedCard) return;

    const service = this.serviceRegistry[this.selectedCard];
    if (service && typeof service.refreshData === 'function') {
      service.refreshData();
    } else {
      // Fallback to loading initial data
      this.loadInitialDataForCard(this.selectedCard);
    }
  }

  /**
   * Handle Date of Return field change
   */
  private onDateOfReturnChange(recordId: string, employeeId: string, dateValue: string): void {
    if (!employeeId || !dateValue) {
      return;
    }

    // Find the record to get the return status
    const record = this.rowData.find(row => row.id === recordId);
    if (!record) {
      return;
    }

    const originalDateOfReturn = record.dateOfReturn;
    const returnStatus = record.returnStatus || 'Returned';

    const rowIndex = this.rowData.findIndex(row => row.id === recordId);
    if (rowIndex !== -1) {
      this.rowData[rowIndex].dateOfReturn = dateValue;
      this.rowData = [...this.rowData];
    }

    this.assetManagementService.updateReturnStatusOffboard({
      employeeId,
      newReturnStatus: returnStatus,
      newReturnDate: dateValue
    }).subscribe({
      next: (response: any) => {
        // Also update original data
        const originalIndex = this.originalRowData.findIndex(row => row.id === recordId);
        if (originalIndex !== -1) {
          this.originalRowData[originalIndex].dateOfReturn = dateValue;
        }
        this.toaster.showSuccess(
          response?.message || 'Date of return updated successfully.',
          'Success',
        );
      },
      error: (error: any) => {
        const revertIndex = this.rowData.findIndex(row => row.id === recordId);
        if (revertIndex !== -1) {
          this.rowData[revertIndex].dateOfReturn = originalDateOfReturn;
          this.rowData = [...this.rowData];
        }
        this.toaster.showError(
          error?.error || 'Failed to update the record.',
          'Error',
        );
      }
    });
  }

  /**
   * Load initial data for specific card types
   */
  private loadInitialDataForCard(card: CardType): void {
    switch (card) {
      case 'damage-analytics':
        this.damageAnalyticsService.loadInitialData();
          this.selectedLocation = this.damageAnalyticsService.getCurrentLocation() || this.selectedLocation;
        break;
      case 'My-Request':
        this.myRequestService.loadInitialData?.();
        break;
      case 'onboarding':
        this.onboardingService.loadInitialData?.();
        break;
      case 'offboarding':
        this.offboardingService.loadInitialData?.();
        break;
      case 'asset-allocated':
        this.assetAllocatedService.loadInitialData?.();
        break;
      case 'inventory':
        this.inventoryService.loadInitialData?.(card, this.selectedLocation);
        this.selectedLocation = this.inventoryService.getCurrentLocation() || this.selectedLocation;
        break;
      case 'inventory-order':
        this.inventoryService.loadInitialData?.(card, this.selectedLocation);
        this.selectedLocation = this.inventoryService.getCurrentProcuredLocation() || this.selectedLocation;
        break;
      case 'vendor-stats':
        this.vendorStatsService.loadInitialData?.();
        break;
      default:
        break;
    }
  }
}